import { FC, useCallback, useMemo, useState } from 'react';
import styled, { css } from 'styled-components';
import { ButtonProps } from './Button.types';
import { buttonBackground } from './constants';
import { Icon } from 'ui/Icon';
import { mobileMediaQuery, notMobileMediaQuery } from 'utils/mediaQuery';

const sizeConfig = {
    s: {
        padding: '8px 12px',
        width: 'initial',
        height: 'initial',
        fontSize: '12px'
    },
    m: {
        width: 'initial',
        height: 'initial',
        padding: '8px',
        fontSize: 'initial'
    },
    base: {
        width: '190px',
        height: '50px',
        padding: '8px 12px',
        fontSize: '18px'
    }
};

type HasChildrenProps = {
    $hasChildren: boolean;
};

const hasChildrenMixin = css<HasChildrenProps>`
    ${({ $hasChildren }) =>
        $hasChildren &&
        notMobileMediaQuery(css`
            margin-right: -24px;
        `)}
`;

const StyledIcon = styled(Icon)<HasChildrenProps>`
    ${hasChildrenMixin}
`;

const StyledIconImg = styled.img<HasChildrenProps>`
    ${hasChildrenMixin}
    height: 24px;
    width: auto;
`;

const StyledContent = styled.div``;

export const StyledButton = styled.button<ButtonProps>`
    // Изменяем с <button> на <a>
    display: flex;
    align-items: center;
    justify-content: center;
    flex-flow: row;
    gap: 12px;
    text-align: center;
    text-decoration: none;
    ${({ size }) => {
        const config = sizeConfig[size || 'base'];
        return css`
            min-width: ${config.width};
            height: ${config.height};
            padding: ${config.padding};
            font-size: ${config.fontSize};
        `;
    }}

    border-radius: 8px;
    background-color: ${({ view: theme }) => buttonBackground(theme)};
    color: #fff;
    font-weight: 500;
    border-width: 0;
    cursor: pointer;

    &:disabled {
        background: gray;
        cursor: default;
    }

    &:not(:disabled):hover {
        background-color: #60779e;
    }

    &:not(:disabled):active {
        background-color: #495b79;
    }

    ${mobileMediaQuery(css`
        &:has(${StyledIcon}),
        &:has(${StyledIconImg}) {
            min-width: unset;
            & ${StyledContent} {
                display: none;
            }
        }
    `)}
`;

// TODO: При указании href помещать в Link

export const Button: FC<ButtonProps> = ({
    disabled,
    children,
    href,
    type,
    icon,
    iconUrl,
    target: rawTarget,
    size,
    onClick,
    ...buttonProps
}) => {
    const target = useMemo(
        () => rawTarget || (buttonProps.download ? '_blank' : undefined),
        [rawTarget, buttonProps.download]
    );

    const [loading, setLoading] = useState(false);

    const handleClick = useCallback(async () => {
        if (!onClick) {
            return;
        }

        const result = onClick();

        if (result instanceof Promise) {
            setLoading(true);
            await result;
            setLoading(false);
        }
    }, [onClick]);

    const finalDisabled = disabled || loading;

    return (
        <StyledButton
            as={href ? 'a' : undefined}
            disabled={finalDisabled}
            href={finalDisabled ? undefined : href}
            type={type || 'button'}
            target={target}
            size={size}
            onClick={handleClick}
            {...buttonProps}
        >
            {children && <StyledContent>{children}</StyledContent>}

            {icon && (
                <StyledIcon $hasChildren={!!children} icon={icon} size={size} />
            )}
            {iconUrl && (
                <StyledIconImg src={iconUrl} $hasChildren={!!children} />
            )}
        </StyledButton>
    );
};

export default Button;
